import { useState, useRef, useEffect } from 'react';
import { Input, AutoComplete, Button, Tooltip, Tag, Spin } from 'antd';
import { SearchOutlined, EyeOutlined } from '@ant-design/icons';
import {  useQuickChargerSearch } from '../../Pages/chargers/Hooks';
import { useTranslation } from 'react-i18next';
import { useOperatorStore } from '../../store/useOperatorStore';
import { LuLogs } from 'react-icons/lu';
import SingleChargerDrawer from '../../Pages/locations/[id]/SingleChargerDrawer';
import { useIsEditAllowed } from '../../utils/utils';
import { useNavigate } from 'react-router-dom';
import {
  buildOcppLogsSearch,
  resolveOcppLogsOperatorId,
} from '../../utils/ocppLogsNavigation';

const statusColors = {
  ONLINE: 'green',
  OFFLINE: 'default',
  FAULTED: 'error',
  CHARGING: 'processing',
};

const QuickChargerSearch = () => {
  const { t } = useTranslation(['quickSearch', 'dashboard']);
  const [search, setSearch] = useState('');
  const { operatorId } = useOperatorStore();
  const navigate = useNavigate();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedCharger, setSelectedCharger] = useState(null);
  const inputRef = useRef(null);
  const isEditAllowed = useIsEditAllowed();
  const { data: chargerData, isLoading } = useQuickChargerSearch(
    { limit: 10, search, offset: 0 },
    operatorId,
    null
  );

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const openChargerDrawer = (chargerId) => {
    setSelectedCharger(chargerId);
    setIsDrawerOpen(true);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setSelectedCharger(null);
  };

  const options =
    chargerData?.data?.paginatedData?.map((charger) => ({
      value: charger.name,
      label: (
        <div className="flex items-center justify-between py-2 px-1 hover:bg-gray-50/80 transition-colors duration-300 cursor-pointer h-full">
          <div className="flex flex-col gap-1 min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium truncate">
                {charger.name}
              </span>
              <Tag
                variant="outlined"
                color={statusColors[charger.status]}
              >
                {charger.status}
              </Tag>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 truncate">
                {charger.serialNumber}
              </span>
              {charger.locationData?.[0]?.locationName && (
                <span className="text-xs text-gray-400 truncate">
                  • {charger.locationData[0].locationName}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2">
            <Tooltip title={t('quickSearch:view')}>
              <Button
                type="text"
                size="small"
                className="flex items-center justify-center !px-2  transition-all duration-300"
                icon={
                  <EyeOutlined className="text-gray-600  transition-colors duration-300" />
                }
                onClick={(e) => {
                  e.stopPropagation();
                  openChargerDrawer(charger._id);
                }}
              />
            </Tooltip>
            {isEditAllowed && (
            <Tooltip title={t('quickSearch:OCPP_logs')}>
              <Button
                type="text"
                size="small"
                className="flex items-center justify-center !px-2  transition-all duration-300"
                icon={<LuLogs className="text-gray-600  transition-colors duration-300" />}
                onClick={(e) => {
                  e.stopPropagation();
                  const ocppOperatorId = resolveOcppLogsOperatorId(
                    operatorId,
                    charger
                  );
                  navigate({
                    pathname: '/dashboard/OCPP-logs',
                    search: buildOcppLogsSearch({
                      chargerId: charger._id,
                      chargerName: charger.name ?? '',
                      operatorId: ocppOperatorId,
                    }),
                    state: {
                      charger: {
                        id: charger._id,
                        name: charger.name ?? '',
                      },
                      operatorId: ocppOperatorId,
                    },
                  });
                }}
              />
            </Tooltip>
            )}
          </div>
        </div>
      ),
    })) || [];

  return (
    <div 
      className="group relative flex flex-col h-full items-center justify-center"
     
    >
      <AutoComplete
        className="w-[240px] group-hover:w-[300px] focus-within:w-[300px] transition-all duration-200 ease-in-out"
        options={ options}
        onSearch={setSearch}
        onSelect={(_/*, option*/) => {
          const charger = chargerData?.data?.paginatedData?.find(c => c.name === _);
          if (charger) {
            openChargerDrawer(charger._id);
          }
        }}
        popupMatchSelectWidth={false}
        classNames={{
          popup: {
            root: '!w-[300px]'
          }
        }}
        notFoundContent={
          isLoading ? (
            <div className="flex justify-center py-4">
              <Spin size="small" />
            </div>
          ) : search ? (
            <div className="text-center py-4 text-gray-500">
              {t('quickSearch:no_chargers_found')}
            </div>
          ) : null
        }
      >
        <Input
          ref={inputRef}
          placeholder={t('quickSearch:search_chargers')}
          prefix={<SearchOutlined className="text-gray-400 transition-colors duration-300" />}
          className="!rounded-lg !bg-white/80 hover:!bg-white focus:!bg-white transition-all duration-300 ease-in-out"
          allowClear
          suffix={<Tag color="default" variant="outlined" style={{ cursor: 'pointer', userSelect: 'none', fontSize: 10 }}>Ctrl K</Tag>}
          onClick={(e) => {
            if (e.target.value) {
              e.target.select();
            }
          }}
        />
      </AutoComplete>
      {isDrawerOpen && (
        <SingleChargerDrawer
          isOpen={isDrawerOpen}
          onClose={closeDrawer}
          selectedCharger={selectedCharger}
        />
      )}
    </div>
  );
};

export default QuickChargerSearch;
